"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { Check } from "lucide-react";
import type { CampaignPageSection } from "@growwithai/shared";
import { formatRupiah } from "@/lib/utils";
import type { LandingPlan } from "../types";
import { sectionBgStyle, sectionHeadingStyle } from "../section-style";
type PricingProps = Extract<CampaignPageSection, {
  type: "pricing";
}>;
function durationLabel(plan: LandingPlan): string {
  if (plan.duration_type === "lifetime") return "akses selamanya";
  if (typeof plan.duration_days === "number" && plan.duration_days > 0) {
    if (plan.duration_days % 365 === 0) {
      const years = plan.duration_days / 365;
      return years === 1 ? "per tahun" : `per ${years} tahun`;
    }
    if (plan.duration_days % 30 === 0) {
      const months = plan.duration_days / 30;
      return months === 1 ? "per bulan" : `per ${months} bulan`;
    }
    return `per ${plan.duration_days} hari`;
  }
  return "";
}

/** Kartu paket membership dari data.plans; CTA menuju checkout /premium. */
export default function PricingSection(t0) {
  const $ = _c(20);
  const {
    section,
    plans
  } = t0;
  if (plans.length === 0) {
    return null;
  }
  const cols = plans.length === 1 ? "max-w-md" : plans.length === 2 ? "sm:grid-cols-2 max-w-3xl" : "sm:grid-cols-2 lg:grid-cols-3";
  let t1;
  if ($[0] !== section.custom_style) {
    t1 = sectionBgStyle(section.custom_style);
    $[0] = section.custom_style;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== section.custom_style) {
    t2 = sectionHeadingStyle(section.custom_style);
    $[2] = section.custom_style;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== section.heading || $[5] !== t2) {
    t3 = <h2 className="text-center text-2xl font-bold tracking-tight text-warm-black md:text-3xl" style={t2}>{section.heading}</h2>;
    $[4] = section.heading;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = `mx-auto mt-8 grid grid-cols-1 gap-5 ${cols}`;
  let t5;
  if ($[7] !== plans) {
    let t6;
    if ($[9] !== plans.length) {
      t6 = (plan, idx) => {
        const highlighted = plans.length > 1 && idx === plans.length - 1;
        const period = durationLabel(plan);
        return <div key={plan.id} className={`relative flex flex-col rounded-2xl border bg-white p-6 shadow-[var(--card-shadow,0_1px_2px_rgba(0,0,0,.05))] [backdrop-filter:var(--card-backdrop,none)] ${highlighted ? "border-[var(--brand-primary)] ring-2 ring-[var(--brand-primary)]/20" : "[border-color:var(--card-border-color,rgba(0,0,0,.08))]"}`}>{highlighted && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[var(--brand-primary)] px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-[var(--brand-on-primary,#fff)]">Paling Populer</span>}<h3 className="font-bold text-warm-black">{plan.name}</h3><div className="mt-3 flex items-baseline gap-2"><span className="text-3xl font-extrabold tracking-tight text-warm-black">{formatRupiah(plan.price)}</span>{period && <span className="text-xs text-gray-500">{period}</span>}</div>{typeof plan.compare_at_price === "number" && plan.compare_at_price > plan.price && <p className="mt-1 text-sm text-gray-500 line-through">{formatRupiah(plan.compare_at_price)}</p>}{plan.description && <ul className="mt-4 space-y-2">{plan.description.split("\n").map(_temp).filter(Boolean).map(_temp2)}</ul>}<Link href="/premium" className={`mt-6 inline-flex h-11 items-center justify-center rounded-xl px-5 text-sm font-semibold transition-colors ${highlighted ? "bg-[var(--brand-primary)] text-[var(--brand-on-primary,#fff)] hover:bg-[var(--brand-primary-hover)]" : "border border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/5"}`}>Pilih Paket</Link></div>;
      };
      $[9] = plans.length;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    t5 = plans.map(t6);
    $[7] = plans;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = <div className={t4}>{t5}</div>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t3 || $[15] !== t6) {
    t7 = <div className="mx-auto max-w-6xl px-5">{t3}{t6}</div>;
    $[14] = t3;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t1 || $[18] !== t7) {
    t8 = <section className="bg-warm-cream py-14 md:py-20" style={t1}>{t7}</section>;
    $[17] = t1;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
}
function _temp2(line_0, i) {
  return <li key={i} className="flex items-start gap-2 text-sm text-gray-600"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--brand-primary)]" /><span>{line_0}</span></li>;
}
function _temp(line) {
  return line.trim();
}